import axios from "axios";
import { saveFile } from "../../../native/files";

const ROOT = `${process.env.REACT_APP_BACKEND_URL}/mail`;
const BASE = `${ROOT}/accounts`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const unwrap = promise => promise.then(res => res.data);

export const getErrorMessage = (error, fallback) =>
  error?.response?.data?.errorMessage || error?.message || fallback;

export const OAUTH_CALLBACK_PATH = "/user/mail/oauth/callback";
export const oauthRedirectUri = () => `${window.location.origin}${OAUTH_CALLBACK_PATH}`;

export const mailApi = {
  getOAuthProviders: () => unwrap(axios.get(`${ROOT}/oauth/providers`, { headers: authHeaders() })),
  startOAuth: (provider, body) =>
    unwrap(axios.post(`${ROOT}/oauth/${provider}/start`, body, { headers: authHeaders() })),
  completeOAuth: body =>
    unwrap(axios.post(`${ROOT}/oauth/complete`, body, { headers: authHeaders() })),

  getAccounts: () => unwrap(axios.get(BASE, { headers: authHeaders() })),
  testAccount: body => unwrap(axios.post(`${BASE}/test`, body, { headers: authHeaders() })),
  createAccount: body => unwrap(axios.post(BASE, body, { headers: authHeaders() })),
  updateAccount: (id, body) => unwrap(axios.put(`${BASE}/${id}`, body, { headers: authHeaders() })),
  deleteAccount: id => unwrap(axios.delete(`${BASE}/${id}`, { headers: authHeaders() })),

  getFolders: id => unwrap(axios.get(`${BASE}/${id}/folders`, { headers: authHeaders() })),
  createFolder: (id, path) =>
    unwrap(axios.post(`${BASE}/${id}/folders`, { path }, { headers: authHeaders() })),
  renameFolder: (id, path, newPath) =>
    unwrap(axios.put(`${BASE}/${id}/folders`, { path, newPath }, { headers: authHeaders() })),
  deleteFolder: (id, path) =>
    unwrap(axios.post(`${BASE}/${id}/folders/delete`, { path }, { headers: authHeaders() })),

  syncMailbox: (id, params) =>
    unwrap(axios.post(`${BASE}/${id}/sync`, params || {}, { headers: authHeaders() })),

  getMessages: (id, params) =>
    unwrap(axios.get(`${BASE}/${id}/messages`, { params, headers: authHeaders() })),
  getMessage: (id, uid, folder) =>
    unwrap(
      axios.get(`${BASE}/${id}/messages/${uid}`, { params: { folder }, headers: authHeaders() })
    ),
  updateFlags: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/messages/flags`, body, { headers: authHeaders() })),
  moveMessages: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/messages/move`, body, { headers: authHeaders() })),
  deleteMessages: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/messages/delete`, body, { headers: authHeaders() })),

  downloadAttachment: async (id, uid, folder, attachment) => {
    const res = await axios.get(`${BASE}/${id}/messages/${uid}/attachments/${attachment.index}`, {
      params: { folder },
      headers: authHeaders(),
      responseType: "blob"
    });
    await saveFile(res.data, attachment.filename);
  },

  // Mail assistant
  getAssistant: id => unwrap(axios.get(`${BASE}/${id}/assistant`, { headers: authHeaders() })),
  setAssistantConsent: (id, enabled) =>
    unwrap(axios.put(`${BASE}/${id}/assistant/consent`, { enabled }, { headers: authHeaders() })),
  getAnalyses: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/assistant/analyses`, body, { headers: authHeaders() })),
  analyze: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/assistant/analyze`, body, { headers: authHeaders() })),
  redraftReply: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/assistant/reply`, body, { headers: authHeaders() })),
  saveAttachments: (id, body) =>
    unwrap(axios.post(`${BASE}/${id}/assistant/attachments`, body, { headers: authHeaders() })),
  getDirectory: () => unwrap(axios.get(`${ROOT}/assistant/directory`, { headers: authHeaders() })),

  // Compose requests are multipart: JSON "payload" plus the uploaded files.
  send: (id, payload, files) =>
    unwrap(
      axios.post(`${BASE}/${id}/send`, toFormData(payload, files), { headers: authHeaders() })
    ),
  saveDraft: (id, payload, files) =>
    unwrap(
      axios.post(`${BASE}/${id}/drafts`, toFormData(payload, files), { headers: authHeaders() })
    )
};

const toFormData = (payload, files = []) => {
  const formData = new FormData();
  formData.append("payload", JSON.stringify(payload));
  files.forEach(file => formData.append("attachments", file));
  return formData;
};

export const startMailOAuth = async (provider, loginHint) => {
  const res = await mailApi.startOAuth(provider, { redirectUri: oauthRedirectUri(), loginHint });
  window.location.assign(res.data.url);
};
